import { useMemo } from "react";

// Define a type for the configuration properties used by the hook
// This makes the hook more reusable and type-safe.
// You might need to adjust this based on the actual structures of
// Presentation and ViewportPresentation if they differ significantly beyond these fields.
type NormalizationConfig =
	| {
			MinRatio?: { Width?: number | null; Height?: number | null } | null;
			MaxRatio?: { Width?: number | null; Height?: number | null } | null;
			MinSize?: { Width?: number | null; Height?: number | null } | null;
			MaxSize?: { Width?: number | null; Height?: number | null } | null;
	  }
	| null
	| undefined;

/**
 * Calculates normalized width and height based on provided dimensions and configuration.
 * It enforces aspect ratio constraints (MinRatio, MaxRatio) and
 * clamps the size based on absolute pixel limits (MinSize, MaxSize) if provided in the config.
 *
 * @param width Current width.
 * @param height Current height.
 * @param config Configuration object containing MinRatio, MaxRatio, MinSize, MaxSize.
 * @returns An object containing the normalized width and height.
 */
export const useNormalizedSize = (width: number, height: number, config: NormalizationConfig) => {
	return useMemo(() => {
		// Return original size if no config or if width/height are invalid
		if (config == null || width <= 0 || height <= 0) {
			return { width, height };
		}

		// --- Aspect Ratio Normalization ---
		const minRatio = (config.MinRatio?.Width ?? width) / (config.MinRatio?.Height ?? height);
		const maxRatio = (config.MaxRatio?.Width ?? width) / (config.MaxRatio?.Height ?? height);
		const currentRatio = width / height;

		let normalizedWidth = width;
		let normalizedHeight = height;

		if (currentRatio > maxRatio) {
			normalizedWidth = height * maxRatio; // Width is too large for height, adjust width
		} else if (currentRatio < minRatio) {
			normalizedHeight = width / minRatio; // Height is too large for width, adjust height
		}

		// --- Min/Max Size Clamping ---
		let finalWidth = normalizedWidth;
		let finalHeight = normalizedHeight;

		if (config.MinSize) {
			const minSizeWidth = config.MinSize.Width ?? 0;
			const minSizeHeight = config.MinSize.Height ?? 0;
			if (finalWidth < minSizeWidth) {
				finalWidth = minSizeWidth;
				// Recalculate height based on clamped width and original ratio, if necessary,
				// or decide if aspect ratio should be broken here.
				// Sticking to the ViewportSize logic for now: just clamp.
			}
			if (finalHeight < minSizeHeight) {
				finalHeight = minSizeHeight;
				// Similarly, recalculate width if height is clamped.
			}
			// Re-evaluate aspect ratio constraint if clamping happened?
			// The original useNormalizedViewportSize didn't seem to re-evaluate ratio after clamping min/max size. Let's stick to that for now.
		}

		if (config.MaxSize) {
			const maxSizeWidth = config.MaxSize.Width ?? Infinity;
			const maxSizeHeight = config.MaxSize.Height ?? Infinity;
			if (finalWidth > maxSizeWidth) {
				finalWidth = maxSizeWidth;
			}
			if (finalHeight > maxSizeHeight) {
				finalHeight = maxSizeHeight;
			}
		}

		// Ensure non-zero dimensions
		finalWidth = Math.max(1, finalWidth);
		finalHeight = Math.max(1, finalHeight);

		return { width: Math.round(finalWidth), height: Math.round(finalHeight) };
	}, [width, height, config]);
};
